import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Settings, Gauge } from 'lucide-react';

export const StreamQualitySettings = () => {
  const [resolution, setResolution] = useState('1080p');
  const [framerate, setFramerate] = useState('60');
  const [bitrate, setBitrate] = useState([6000]);
  const [lowLatency, setLowLatency] = useState(true);
  const [adaptiveBitrate, setAdaptiveBitrate] = useState(true);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Stream Quality
        </CardTitle>
        <CardDescription>
          Configure streaming quality and performance settings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>Resolution</Label>
          <Select value={resolution} onValueChange={setResolution}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2160p">4K (2160p)</SelectItem>
              <SelectItem value="1440p">2K (1440p)</SelectItem>
              <SelectItem value="1080p">Full HD (1080p)</SelectItem>
              <SelectItem value="720p">HD (720p)</SelectItem>
              <SelectItem value="480p">SD (480p)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Frame Rate</Label>
          <Select value={framerate} onValueChange={setFramerate}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="60">60 FPS</SelectItem>
              <SelectItem value="30">30 FPS</SelectItem>
              <SelectItem value="25">25 FPS</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Bitrate</Label>
            <span className="text-sm text-muted-foreground">{bitrate[0]} kbps</span>
          </div>
          <Slider
            value={bitrate}
            onValueChange={setBitrate}
            min={1000}
            max={15000}
            step={500}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>1 Mbps</span>
            <span>15 Mbps</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Low Latency Mode</Label>
            <p className="text-xs text-muted-foreground">
              Reduce stream delay for real-time interaction
            </p>
          </div>
          <Switch checked={lowLatency} onCheckedChange={setLowLatency} />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Adaptive Bitrate</Label>
            <p className="text-xs text-muted-foreground">
              Automatically adjust quality based on connection
            </p>
          </div>
          <Switch checked={adaptiveBitrate} onCheckedChange={setAdaptiveBitrate} />
        </div>

        <div className="bg-muted p-4 rounded-lg space-y-2">
          <div className="flex items-center gap-2">
            <Gauge className="h-4 w-4" />
            <h4 className="font-semibold text-sm">Recommended Settings</h4>
          </div>
          <ul className="text-xs space-y-1 text-muted-foreground">
            <li>• 1080p @ 60fps: 6000-8000 kbps</li>
            <li>• 1080p @ 30fps: 4500-6000 kbps</li>
            <li>• 720p @ 60fps: 4500-6000 kbps</li>
            <li>• 720p @ 30fps: 3000-4500 kbps</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
